import axiosInstance from "@/setup/axios/axiosInstance";
import { TransactionType, CreateTransactionTypeRequest, UpdateTransactionTypeRequest } from '../../../finance/models/TransactionType';

// Backend response interface
interface BackendTransactionType {
  id: string;
  code: string;
  name: string;
  color?: string;
  order_number: number;
  type: 'payment' | 'receipt';  // Backend uses 'type' field
  description?: string;
  is_active: boolean;
  use_in_report?: string[];
  parent_id?: string | null;
}

// Transform backend data to frontend format
const transformBackendToFrontend = (backendData: BackendTransactionType): TransactionType => {
  return {
    id: backendData.id,
    code: backendData.code,
    name: backendData.name,
    color: backendData.color || '',
    description: backendData.description,
    category: backendData.type === 'payment' ? 'expense' : 'income', // Map backend 'type' to frontend 'category'
    is_active: backendData.is_active,
    order_number: backendData.order_number,
    use_in_report: Array.isArray(backendData.use_in_report) ? backendData.use_in_report : [],
    parent_id: backendData.parent_id ? String(backendData.parent_id) : null,
    created_at: new Date().toISOString(), // Backend doesn't have these fields yet
    created_by: 'system',
    updated_at: new Date().toISOString(),
    updated_by: 'system',
  };
};

// Transform frontend data to backend format
const transformFrontendToBackend = (frontendData: CreateTransactionTypeRequest | UpdateTransactionTypeRequest): any => {
  const useInReport = Array.isArray(frontendData.use_in_report)
    ? [...frontendData.use_in_report]
    : [];

  const baseData = {
    code: frontendData.code,
    name: frontendData.name,
    color: frontendData.color || '',
    description: frontendData.description,
    type: frontendData.category === 'income' ? 'receipt' : 'payment', // Map frontend 'category' to backend 'type'
    is_active: frontendData.is_active,
    order_number: frontendData.order_number || 0,
    // Keep JSONField payload as a plain JSON array in request body.
    use_in_report: useInReport,
    parent_id: frontendData.parent_id ?? null,
  };

  if ('id' in frontendData) {
    return { ...baseData, id: frontendData.id };
  }
  return baseData;
};

export type TransactionTypeListFilters = {
  type?: 'payment' | 'receipt';
  no_pagination?: boolean;
};

// Get transaction types
export const getTransactionTypes = async (filters?: TransactionTypeListFilters) => {
  try {
    const response = await axiosInstance.get('/finance/transaction-types/', {
      params: {
        no_pagination: filters?.no_pagination ?? true,
        ...(filters?.type ? { type: filters.type } : {}),
      },
    });
    
    // Handle different response formats
    let data: BackendTransactionType[];
    
    if (Array.isArray(response.data)) {
      // Direct array response
      data = response.data;
    } else if (response.data && Array.isArray(response.data.results)) {
      // Paginated response with results
      data = response.data.results;
    } else if (response.data && Array.isArray(response.data.data)) {
      // Response with data property
      data = response.data.data;
    } else {
      return { data: [] };
    }
    
    const transformedData = data.map(transformBackendToFrontend);
    
    return { data: transformedData };
  } catch (error: any) {
    throw error;
  }
};

// Get transaction type by ID
export const getTransactionTypeById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/finance/transaction-types/${id}/`);
    return { data: transformBackendToFrontend(response.data) };
  } catch (error: any) {
    throw error;
  }
};

// Create transaction type
export const createTransactionType = async (transactionTypeData: CreateTransactionTypeRequest) => {
  try {
    const backendData = transformFrontendToBackend(transactionTypeData);
    const response = await axiosInstance.post('/finance/transaction-types/', backendData);
    return { data: transformBackendToFrontend(response.data) };
  } catch (error: any) {
    throw error;
  }
};

// Update transaction type
export const updateTransactionType = async (transactionTypeData: UpdateTransactionTypeRequest) => {
  try {
    const { id } = transactionTypeData;
    const backendData = transformFrontendToBackend(transactionTypeData);
    const response = await axiosInstance.put(`/finance/transaction-types/${id}/`, backendData);
    return { data: transformBackendToFrontend(response.data) };
  } catch (error: any) {
    throw error;
  }
};

export interface BulkUpsertTransactionTypeItem {
  id?: string | null;
  name: string;
  code: string;
  color?: string;
  description?: string;
  category: 'income' | 'expense';
  is_active: boolean;
  order_number?: number;
  use_in_report?: string[];
  parent_id?: string | null;
}

// Bulk upsert transaction types (single request)
export const bulkUpsertTransactionTypes = async (transactionTypeItems: BulkUpsertTransactionTypeItem[]) => {
  try {
    const items = transactionTypeItems.map((item) => transformFrontendToBackend(item as any));
    const response = await axiosInstance.post('/finance/transaction-types/bulk-upsert/', { items });

    // Normalize response to frontend shape when backend returns updated records
    const rawItems = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

    return {
      data: rawItems.map((item: BackendTransactionType) => transformBackendToFrontend(item)),
    };
  } catch (error: any) {
    throw error;
  }
};

// Delete transaction type
export const deleteTransactionType = async (id: string) => {
  try {
    await axiosInstance.delete(`/finance/transaction-types/${id}/`);
    return { data: id };
  } catch (error: any) {
    throw error;
  }
};

// Bulk delete transaction types
export const deleteTransactionTypes = async (ids: string[]) => {
  try {
    return { data: ids };
  } catch (error: any) {
    throw error;
  }
};

// Soft delete transaction types
export const softDeleteTransactionTypes = async (transactionTypeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transaction-types/soft_delete/", {
      transaction_type_ids: transactionTypeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted transaction types
export const restoreTransactionTypes = async (transactionTypeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transaction-types/restore_deleted/", {
      transaction_type_ids: transactionTypeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete transaction types
export const permanentDeleteTransactionTypes = async (transactionTypeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/finance/transaction-types/permanent_delete/", {
      transaction_type_ids: transactionTypeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get deleted transaction types
export const getDeletedTransactionTypes = async () => {
  try {
    const response = await axiosInstance.get("/finance/transaction-types/?show_deleted=true");
    return response;
  } catch (error) {
    throw error;
  }
};

